import { useEffect, useState } from 'react';
import { Form, Button, Spin, Typography, message, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useOperatorStore } from '../../store/useOperatorStore';
import {
  useGetPaymentDetails,
  useCreatePaymentDetails,
  useUpdatePaymentDetails,
} from '../operators/Hooks';
import PaymentDetailsModal from '../../components/Modals/PaymentDetailsModal';
import { getErrorMessage } from '../../utils/errorHandler';

/**
 * Payment gateway credentials for the single default operator.
 */
export default function PaymentGatewaySettingsTab() {
  const { t } = useTranslation(['settings', 'operator', 'common']);
  const { operatorId } = useOperatorStore();
  const [modalOpen, setModalOpen] = useState(false);

  const {
    data: paymentDetails,
    isLoading,
    isError,
    refetch,
  } = useGetPaymentDetails({
    id: operatorId,
    options: { enabled: !!operatorId },
  });

  const createMutation = useCreatePaymentDetails({
    onSuccess: () => {
      message.success(t('operator:paymentDetailsCreateSuccess', { defaultValue: 'Payment details saved' }));
      refetch();
      setModalOpen(false);
    },
    onError: (err) => {
      message.error(getErrorMessage(err, t('common:updateError')));
    },
  });

  const updateMutation = useUpdatePaymentDetails({
    onSuccess: () => {
      message.success(t('operator:paymentDetailsUpdateSuccess', { defaultValue: 'Payment details updated' }));
      refetch();
      setModalOpen(false);
    },
    onError: (err) => {
      message.error(getErrorMessage(err, t('common:updateError')));
    },
  });

  const isEdit = !!paymentDetails?.paymentGateway;

  const handleSubmit = async (values) => {
    if (!operatorId) return;
    if (isEdit) {
      await updateMutation.mutateAsync({ id: operatorId, data: values });
    } else {
      await createMutation.mutateAsync({ id: operatorId, data: values });
    }
  };

  useEffect(() => {
    // no-op; ensures hook order stable
  }, [operatorId]);

  if (!operatorId) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('paymentGatewayNeedsOperator', {
          defaultValue: 'Default operator is not bound yet.',
        })}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={t('common:fetchError')}
      />
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <Typography.Title level={5}>
        {t('paymentGatewayTab', { defaultValue: 'Payment Gateway' })}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {t('paymentGatewayHint', {
          defaultValue:
            'Configure payment gateway credentials used for charging payments.',
        })}
      </Typography.Paragraph>

      {isEdit ? (
        <Form layout="vertical">
          <Form.Item label={t('operator:paymentGateway', { defaultValue: 'Gateway' })}>
            <Typography.Text>{paymentDetails.paymentGateway}</Typography.Text>
          </Form.Item>
          <Form.Item label="SX">
            <Typography.Text code>{paymentDetails.SX ? '••••••••' : '—'}</Typography.Text>
          </Form.Item>
          <Button type="primary" onClick={() => setModalOpen(true)}>
            {t('operator:paymentDetailsUpdateTitle', { defaultValue: 'Update payment details' })}
          </Button>
        </Form>
      ) : (
        <Button type="primary" onClick={() => setModalOpen(true)}>
          {t('operator:paymentDetailsCreateTitle', { defaultValue: 'Add payment details' })}
        </Button>
      )}

      <PaymentDetailsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        initialData={isEdit ? paymentDetails : null}
        isEdit={isEdit}
      />
    </div>
  );
}
